import React, { useState, useEffect } from 'react';
import { Avatar, Dropdown } from 'antd';
import { FontSizeOutlined } from '@ant-design/icons';
import { useAuthContext } from '@/hooks/auth';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import { signOut } from '@/api/api';
import {
  getCurrentPlan,
  getBoughtProductList,
  getCurrentPlanNew,
  getProductListWithPricing,
  getSubscriptionProducts,
  getSummerPromotionInfo,
  getFirstMonthPromotionInfo,
  createOrder,
  completeOrder,
  cancelOrder,
  cancelSubscription,
  restoreSubscription,
  getStripePortalUrl,
  checkEnterpriseUser,
} from '@/api/subtitles';
import type { MenuProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import SvgIcon from '@/components/SvgIcon';
import Empty from '@/components/record/empty';
import dayjs from 'dayjs';
import Decimal from 'decimal.js';
import clsx from 'clsx';
import { Table, Pagination, Spin, message, Modal, Select } from 'antd';
import { getAvailableFonts, getFontPreference, saveFontPreference } from '@/utils/fontPreference';
import { trackEvent, TrackingEvent } from '@/utils/tracking';
import { reportCustomLog } from '@/utils/logReporter';
import { getShareSourceFromUrl } from '@/utils/shareSource';
import { readAndPersistUtmSource, getUtmSource } from '@/utils/utmSource';

// 导入新组件
import {
  SubscriptionOverview,
  PlanSelection,
  BillingHistory,
  UsageHistory,
  CancelSubscriptionModal,
  CancellationReasonModal,
  TabButton,
  Settings,
  plans,
  addonPackages,
  productNameMap,
  productPaymentMap,
} from './enhanced-components';

import {
  convertProductsToAddonPackages,
  getCurrency,
  convertSubscriptionProductsToPlans,
} from './enhanced-components/utils';

import type { Subscription, TabType, PlanCategory } from './enhanced-components';

import safetyIcon from '@/assets/icons/safety.svg';
import userInfoIcon from '@/assets/icons/userInfo.svg';
import arrowRighticon from '@/assets/icons/arrow-right.svg';
import styles from '@/components/style/index.module.less';
import './components/index.less';
import './enhanced-components/enhanced-styles.less';

function EnhancedAccount() {
  const { t, i18n } = useTranslation();
  const { state } = useAuthContext();
  const { user } = state;

  // State management
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [billSubTab, setBillSubTab] = useState(0);
  const [total, setTotal] = useState(0);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [planCategory, setPlanCategory] = useState<PlanCategory>('personal');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [hideActionPanel, setHideActionPanel] = useState(false);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const [restoringSubscription, setRestoringSubscription] = useState(false);

  // Usage pagination state
  const [usageCurr, setUsageCurr] = useState(1);
  const [usageTotal, setUsageTotal] = useState(0);
  const [usageSize, setUsageSize] = useState(10);

  // Buy history pagination state
  const [buyHistoryCurr, setBuyHistoryCurr] = useState(1);
  const [buyHistoryTotal, setBuyHistoryTotal] = useState(0);
  const [buyHistorySize, setBuyHistorySize] = useState(10);

  // Mock subscription data
  const subscription: Subscription = {
    id: 'sub_1234567890',
    plan: 'Pro Plan',
    status: 'active',
    amount: 29.99,
    interval: 'month',
    currentPeriodEnd: '2024-02-15',
    cancelAtPeriodEnd: false,
  };

  // Payment handling function
  const handleChangePlan = async () => {
    if (!selectedPlan) return;

    try {
      setProcessingPayment(true);

      // Get the selected product details
      const currency = getCurrency(i18n.language);

      // Create order and redirect to payment
      const response = await createOrder(selectedPlan, currency, getUtmSource());

      if (response.data?.data) {
        // Redirect to payment provider
        window.location.href = response.data.data;
      } else {
        console.error('Failed to create payment order');
      }
    } catch (error) {
      console.error('Payment initialization failed:', error);
    } finally {
      setProcessingPayment(false);
    }
  };

  // Stripe portal redirect handler
  const handleStripePortalRedirect = () => {
    const stripeUrl = getStripePortalUrlRequest.data?.data?.data;
    if (stripeUrl) {
      // 上报访问支付页面事件
      trackEvent(TrackingEvent.VISIT_CHECKOUT, getShareSourceFromUrl());
      reportCustomLog('VisitCheckout', user?.uid);
      // 在新页面中打开Stripe门户
      window.open(stripeUrl, '_blank');
    } else {
      message.error(t('account.failedToRedirectToStripe') || 'Failed to redirect to subscription management');
    }
  };

  // Event handlers
  const openSetClick = () => {
    const path = `${import.meta.env.VITE_FELO_API_URL}/account`;
    window.open(path);
  };

  const openSafetyClick = () => {
    const path = `${import.meta.env.VITE_FELO_API_URL}/account/security`;
    window.open(path);
  };

  const logoutClick = () => {
    signOut();
    Cookies.remove(import.meta.env.VITE_COOKIE_SUFFIX);
    Cookies.remove('subtitlestoken');
    window.location.href = import.meta.env.VITE_FELO_HOME_URL;
  };

  const openPage = (url: string) => {
    window.open(import.meta.env.VITE_FELO_SUBTITLES_URL + url);
  };

  // Get plan balance data
  const getPlanBalanceData = async () => {
    const res = await getCurrentPlan();
    if (res.data.data) {
      setTotal(Math.floor(res.data.data.balance / 60));
    }
  };

  useEffect(() => {
    readAndPersistUtmSource();
  }, []);

  // Handle payment callbacks
  useEffect(() => {
    const searchObj = new URLSearchParams(location.search);
    const paymentStatus = searchObj.get('payment');
    const productId = searchObj.get('product_id');
    const checkoutId = searchObj.get('checkout_id');
    const receipt = searchObj.get('receipt');

    if (paymentStatus === 'succeed' && checkoutId && receipt) {
      // Payment successful
      completeOrder(checkoutId, receipt)
        .then(() => {
          message.success(t('account.paySucceed') || 'Payment successful!');
          // Refresh plan data
          getCurrentPlanRequest.refetch();
          // Clear URL parameters
          window.history.replaceState({}, '', '/account');
        })
        .catch(() => {
          message.error('Failed to complete payment verification');
        });
    } else if (paymentStatus === 'failed' && checkoutId) {
      // Payment failed
      cancelOrder(checkoutId)
        .then(() => {
          message.error(t('account.payFailed') || 'Payment failed. Please try again.');
          // Clear URL parameters
          window.history.replaceState({}, '', '/account');
        })
        .catch(() => {
          message.error('Failed to cancel payment order');
        });
    }
  }, []);

  useEffect(() => {
    getPlanBalanceData();
  }, []);

  // 处理 URL 查询参数，支持直接跳转到指定 tab
  // 使用方式: /account?tab=plan 或 /account?tab=addon
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const tab = searchParams.get('tab');

    const shareSource = getShareSourceFromUrl();
    if (tab === 'plan') {
      setActiveTab('change-plan');
      trackEvent(TrackingEvent.VISIT_PRICE, shareSource);
      reportCustomLog('VisitPrice', user?.uid);
    } else if (tab === 'addon') {
      setActiveTab('change-plan');
      setPlanCategory('addon');
      trackEvent(TrackingEvent.VISIT_PRICE, shareSource);
      reportCustomLog('VisitPrice', user?.uid);
    }
  }, []);

  // Buy history table columns
  const buyHistoryColumns: ColumnsType<API.PlanListItem> = [
    {
      title: t('typePackage'),
      dataIndex: 'product_id',
      render: (text) => (productNameMap[text] ? t(productNameMap[text]) : text),
    },
    {
      title: t('modePayment'),
      dataIndex: 'source',
      render: (text) => (productPaymentMap[text] ? t(productPaymentMap[text]) : text),
    },
    {
      title: t('quota'),
      dataIndex: 'total',
      render: (value) => t('minutes', { time: new Decimal(value).div(60).ceil().toString() }),
    },
    {
      title: t('buyTime'),
      dataIndex: 'start_time',
      render: (text) => dayjs(text).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: t('expirationTime'),
      dataIndex: 'expire_time',
      render: (text) => (text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : ''),
    },
  ];

  // Queries
  const getBoughtProductRequest = useQuery({
    queryKey: ['getBoughtProduct', buyHistoryCurr, buyHistorySize],
    queryFn: () =>
      getBoughtProductList(buyHistoryCurr, buyHistorySize).then((res) => {
        if (res.data.data.total) {
          setBuyHistoryTotal(res.data.data.total);
        }
        return res;
      }),
  });

  // 获取当前计划数据
  const getCurrentPlanRequest = useQuery({
    queryKey: ['getCurrentPlan'],
    queryFn: () => getCurrentPlanNew(),
  });

  // 获取产品列表数据
  const getProductListRequest = useQuery({
    queryKey: ['getProductList'],
    queryFn: () => getProductListWithPricing(),
  });

  // 获取订阅产品列表
  const getSubscriptionProductsRequest = useQuery({
    queryKey: ['getSubscriptionProducts'],
    queryFn: () => getSubscriptionProducts(),
  });

  // 夏季促销活动信息（后台必登录，Authorization 按账号判定资格）
  const getSummerPromotionRequest = useQuery({
    queryKey: ['getSummerPromotion'],
    queryFn: () => getSummerPromotionInfo(),
    staleTime: 60 * 1000,
  });
  const summerPromotionInfo = getSummerPromotionRequest.data?.data;
  const summerPromotionOn = !!(summerPromotionInfo?.active && summerPromotionInfo?.eligible);
  const summerDiscountMap: Record<string, number> = {};
  if (summerPromotionOn) {
    (summerPromotionInfo?.items || []).forEach((item) => {
      summerDiscountMap[item.product_id] = item.percent_off;
    });
  }
  const summerMaxPercentOff = Math.max(0, ...Object.values(summerDiscountMap));

  // 首月半价活动（与夏促时间互斥，服务端保证不叠加）
  const getFirstMonthPromotionRequest = useQuery({
    queryKey: ['getFirstMonthPromotion'],
    queryFn: () => getFirstMonthPromotionInfo(),
  });
  const firstMonthPromotionInfo = getFirstMonthPromotionRequest.data?.data;
  const firstMonthOn = !!(
    !summerPromotionOn &&
    firstMonthPromotionInfo?.active &&
    firstMonthPromotionInfo?.eligible
  );

  // 判断当前用户是否为企业用户（企业用户隐藏充值包）
  const enterpriseCheckRequest = useQuery({
    queryKey: ['checkEnterpriseUser'],
    queryFn: () => checkEnterpriseUser(),
  });
  const isEnterpriseUser = !!enterpriseCheckRequest.data?.data.data?.enterprise_user;

  // 判断用户是否有订阅计划
  const hasSubscription = () => {
    const currentPlanData = getCurrentPlanRequest.data?.data.data;
    if (!currentPlanData) return false;

    // 检查type是否为SUBSCRIPTION且有plan字段
    return currentPlanData.type === 'SUBSCRIPTION' && !!currentPlanData.plan;
  };

  // 预获取Stripe门户链接 - 只有当用户有订阅计划时才获取
  const getStripePortalUrlRequest = useQuery({
    queryKey: ['getStripePortalUrl'],
    queryFn: () => getStripePortalUrl(`${window.location.origin}/account`),
    staleTime: 5 * 60 * 1000, // 5分钟内不重新获取
    gcTime: 10 * 60 * 1000, // 缓存10分钟
    enabled: hasSubscription(), // 只有当用户有订阅计划时才启用这个查询
  });

  // Event handlers
  const handleTabChange = (tab: TabType) => {
    // 如果点击"更改计划"tab，上报访问定价页面事件
    if (tab === 'change-plan') {
      trackEvent(TrackingEvent.VISIT_PRICE, getShareSourceFromUrl());
      reportCustomLog('VisitPrice', user?.uid);
    }

    setActiveTab(tab);
    // 当切换到其他tab时，重置hideActionPanel状态
    if (tab !== 'change-plan') {
      setHideActionPanel(false);
    }
  };

  const handlePlanCategoryChange = (category: PlanCategory) => {
    setPlanCategory(category);
    // 当切换计划分类时，重置hideActionPanel状态
    setHideActionPanel(false);
    // 同时重置选择的计划
    setSelectedPlan(null);
  };

  const handlePlanSelect = (planId: string) => {
    setSelectedPlan(planId);
    // 当选择计划时，检查是否为当前计划，如果不是则显示操作面板
    const currentProductId = getCurrentPlanRequest.data?.data.data?.plan?.product_id || '';
    if (planId !== currentProductId) {
      setHideActionPanel(false);
    }
  };

  const handleCancelSelection = () => {
    setSelectedPlan(null);
  };

  const handleShowCancelModal = () => {
    // 显示取消订阅确认弹窗
    setShowCancelModal(true);
  };

  const handleReasonModalClose = () => {
    setShowReasonModal(false);
  };

  const handleReasonSubmitSuccess = () => {
    // 取消理由提交成功后，只关闭理由弹窗
    setShowReasonModal(false);
  };

  const handleCancelModalClose = () => {
    setShowCancelModal(false);
  };

  const handleCancelSubscription = async () => {
    // 获取当前订阅ID
    const currentPlanData = getCurrentPlanRequest.data?.data.data;
    const subscriptionId = currentPlanData?.plan?.subscription_id || currentPlanData?.plan?.id;

    if (!subscriptionId) {
      message.error(t('account.noSubscriptionToCancel') || 'No subscription to cancel');
      setShowCancelModal(false);
      return;
    }

    try {
      setCancellingSubscription(true);

      await cancelSubscription(subscriptionId);

      message.success(t('account.subscriptionCancelledSuccessfully') || 'Subscription cancelled successfully');
      setShowCancelModal(false);

      // 重新获取当前计划数据以刷新UI
      getCurrentPlanRequest.refetch();

      // 取消订阅成功后，显示取消理由收集弹窗
      setShowReasonModal(true);
    } catch (error: any) {
      console.error('Cancel subscription error:', error);
      message.error(t('account.failedToCancelSubscription') || 'Failed to cancel subscription. Please try again.');
    } finally {
      setCancellingSubscription(false);
    }
  };

  const handleRestoreSubscription = async () => {
    // 获取当前订阅ID
    const currentPlanData = getCurrentPlanRequest.data?.data.data;
    const subscriptionId = currentPlanData?.plan?.subscription_id || currentPlanData?.plan?.id;

    if (!subscriptionId) {
      message.error(t('account.noSubscriptionToRestore') || 'No subscription to restore');
      return;
    }

    try {
      setRestoringSubscription(true);

      await restoreSubscription(subscriptionId);

      message.success(t('account.subscriptionRestoredSuccessfully') || 'Subscription restored successfully');

      // 重新获取当前计划数据以刷新UI
      getCurrentPlanRequest.refetch();
    } catch (error: any) {
      console.error('Restore subscription error:', error);
      message.error(t('account.failedToRestoreSubscription') || 'Failed to restore subscription. Please try again.');
    } finally {
      setRestoringSubscription(false);
    }
  };

  const handleUsagePaginationChange = (page: number, pageSize: number) => {
    setUsageCurr(page);
    setUsageSize(pageSize);
  };

  // 字体选择(齿轮菜单-字体):设备级设置,存 localStorage 不走后端(见 utils/fontPreference.ts)
  const [fontModalOpen, setFontModalOpen] = useState(false);
  const [fontPref, setFontPref] = useState<string>(() => getFontPreference());
  // 只列本机实际可用的字体,每项用自身字体渲染预览
  const [availableFonts] = useState(() => getAvailableFonts());

  // Dropdown menu items
  const dropdownItems: MenuProps['items'] = [
    {
      key: 'account',
      label: (
        <div className={`${styles.userDataContentMenu} user-info-section`}>
          <Avatar
            className={`${styles.felosubtitleavatar} user-avatar`}
            style={{
              backgroundColor: user?.picture?.indexOf('http') >= 0 && user.picture ? 'transparent' : '#7F67F5',
              background:
                user?.picture?.indexOf('http') >= 0 && user.picture
                  ? 'transparent'
                  : 'linear-gradient(135deg, #7F67F5 0%, #9C8AFF 100%)',
              color: 'white',
              fontWeight: 600,
            }}
            size={44}
            src={user?.picture?.indexOf('http') >= 0 && user.picture ? user.picture : null}
          >
            {user?.email?.charAt(0)?.toLocaleUpperCase() || user?.name?.charAt(0)?.toLocaleUpperCase() || 'U'}
          </Avatar>
          <div className={styles.userDataContentInfo}>
            <div className={styles.useName}>{user?.name}</div>
            <div className={styles.userEmail}>{user?.email}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'userInfo',
      label: (
        <div className={styles.menupropsItem} onClick={openSetClick}>
          <img src={userInfoIcon} className={styles.icon} alt="User Info" />
          <div className={styles.text}>{t('userInfo')}</div>
          <img src={arrowRighticon} className={styles.rightIcon} alt="Arrow" />
        </div>
      ),
    },
    {
      key: 'safety',
      label: (
        <div className={styles.menupropsItem} onClick={openSafetyClick}>
          <img src={safetyIcon} className={styles.icon} alt="Safety" />
          <div className={styles.text}>{t('safety')}</div>
          <img src={arrowRighticon} className={styles.rightIcon} alt="Arrow" />
        </div>
      ),
    },
    {
      key: 'font',
      label: (
        <div className={styles.menupropsItem} onClick={() => setFontModalOpen(true)}>
          <FontSizeOutlined className={styles.icon} />
          <div className={styles.text}>{t('settings.font')}</div>
          <img src={arrowRighticon} className={styles.rightIcon} alt="Arrow" />
        </div>
      ),
    },
    {
      key: 'logout',
      label: <div className={styles.logoutItem}>{t('logOut')}</div>,
      onClick: async () => {
        logoutClick();
      },
    },
  ];

  return (
    <div className={styles.pageContent}>
      {/* Header */}
      <div className={styles.pageTitle}>
        <div>{t('accountManagement')}</div>
        <div className={styles.pageTitleRight}>
          <Dropdown menu={{ items: dropdownItems }} overlayClassName={styles.dropdownCustom} placement="bottomRight">
            <div className={styles.iconItem}>
              <SvgIcon name="set" />
            </div>
          </Dropdown>
        </div>
      </div>

      {/* 字体偏好弹窗(齿轮菜单-字体) */}
      <Modal
        open={fontModalOpen}
        onCancel={() => setFontModalOpen(false)}
        footer={null}
        title={t('settings.font')}
        width={420}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0' }}>
          <span>{t('settings.font')}</span>
          <Select
            value={fontPref}
            style={{ width: 220 }}
            onChange={(v: string) => {
              setFontPref(v);
              saveFontPreference(v);
            }}
            options={[
              { value: 'default', label: t('settings.fontDefault') },
              ...availableFonts.map((f) => ({
                value: f.id,
                label: <span style={{ fontFamily: f.css }}>{f.label}</span>,
              })),
            ]}
          />
        </div>
        <div style={{ color: '#8c8c8c', fontSize: 12, marginTop: 4 }}>{t('settings.fontDesc')}</div>
      </Modal>

      <div className={styles.userData}>
        <div className={styles.userTitle}>{t('userData')}</div>

        {/* User Info Section */}
        <div className={`${styles.userDataContent} user-info-section`}>
          <Avatar
            className={`${styles.felosubtitleavatar} user-avatar`}
            style={{
              backgroundColor: user?.picture?.indexOf('http') >= 0 && user.picture ? 'transparent' : '#7F67F5',
              background:
                user?.picture?.indexOf('http') >= 0 && user.picture
                  ? 'transparent'
                  : 'linear-gradient(135deg, #7F67F5 0%, #9C8AFF 100%)',
              color: 'white',
              fontWeight: 600,
            }}
            size={44}
            src={user?.picture?.indexOf('http') >= 0 && user.picture ? user.picture : null}
          >
            {user?.email?.charAt(0)?.toLocaleUpperCase() || user?.name?.charAt(0)?.toLocaleUpperCase() || 'U'}
          </Avatar>
          <div className={styles.userDataContentInfo}>
            <div className={styles.useName}>{user?.name}</div>
            <div className={styles.userEmail}>{user?.email}</div>
          </div>
        </div>

        <div className={styles.pageborder}></div>

        {/* Enhanced Billing Section with Tabs */}
        <div className="enhanced-billing p-4">
          {/* Main Navigation Tabs */}
          <div className="enhanced-tabs">
            <TabButton
              id="overview"
              label={t('account.overview') || 'Overview'}
              isActive={activeTab === 'overview'}
              onClick={() => handleTabChange('overview')}
            />
            <TabButton
              id="billing"
              label={t('account.billingHistory') || 'Billing History'}
              isActive={activeTab === 'billing'}
              onClick={() => handleTabChange('billing')}
            />
            <TabButton
              id="usage-history"
              label={t('account.usageHistory') || 'Usage History'}
              isActive={activeTab === 'usage-history'}
              onClick={() => handleTabChange('usage-history')}
            />
            <TabButton
              id="change-plan"
              label={t('account.changePlan') || 'Change Plan'}
              isActive={activeTab === 'change-plan'}
              onClick={() => handleTabChange('change-plan')}
            />
            {/* 暂时隐藏设置tab，下个版本再上线
            <TabButton
              id="settings"
              label={t('account.settings') || 'Settings'}
              isActive={activeTab === 'settings'}
              onClick={() => handleTabChange('settings')}
            />
            */}
            {/* <TabButton
              id="bill-management"
              label={t('account.billManagement') || 'Bill Management'}
              isActive={activeTab === 'bill-management'}
              onClick={() => handleTabChange('bill-management')}
            /> */}
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <SubscriptionOverview
              subscription={subscription}
              currentPlanData={getCurrentPlanRequest.data?.data.data}
              subscriptionProducts={getSubscriptionProductsRequest.data?.data || []}
              isLoading={getCurrentPlanRequest.isLoading || getSubscriptionProductsRequest.isLoading}
              onChangeTab={handleTabChange}
              onChangePlanCategory={handlePlanCategoryChange}
              onShowCancelModal={handleShowCancelModal}
              onSetHideActionPanel={setHideActionPanel}
              onRestoreSubscription={handleRestoreSubscription}
              restoringSubscription={restoringSubscription}
              isEnterpriseUser={isEnterpriseUser}
            />
          )}

          {activeTab === 'billing' && (
            <BillingHistory
              purchaseRecords={getBoughtProductRequest.data?.data.data.records || []}
              subscriptionProducts={getSubscriptionProductsRequest.data?.data || []}
              addonProducts={getProductListRequest.data?.data || []}
              isLoading={getBoughtProductRequest.isLoading}
              onStripePortalRedirect={handleStripePortalRedirect}
              stripeUrl={getStripePortalUrlRequest.data?.data?.data}
              hasSubscription={hasSubscription()}
              userId={user?.uid}
            />
          )}

          {activeTab === 'usage-history' && (
            <UsageHistory
              usageCurr={usageCurr}
              usageTotal={usageTotal}
              usageSize={usageSize}
              onUsagePaginationChange={handleUsagePaginationChange}
            />
          )}

          {activeTab === 'change-plan' && (
            <PlanSelection
              plans={
                getSubscriptionProductsRequest.data?.data
                  ? convertSubscriptionProductsToPlans(
                      getSubscriptionProductsRequest.data.data,
                      t,
                      getCurrency(i18n.language),
                      summerPromotionOn ? summerDiscountMap : undefined,
                      firstMonthOn
                        ? {
                            percentOff: firstMonthPromotionInfo?.percent_off || 50,
                            productIds: firstMonthPromotionInfo?.product_ids || [],
                          }
                        : undefined,
                    )
                  : plans
              }
              summerPromotion={
                summerPromotionOn && summerMaxPercentOff > 0
                  ? { maxPercentOff: summerMaxPercentOff, endTime: summerPromotionInfo?.end_time }
                  : null
              }
              firstMonthPromotion={
                firstMonthOn
                  ? {
                      percentOff: firstMonthPromotionInfo?.percent_off || 50,
                      endTime: firstMonthPromotionInfo?.end_time,
                    }
                  : null
              }
              addonPackages={
                getProductListRequest.data?.data
                  ? convertProductsToAddonPackages(getProductListRequest.data.data, t, getCurrency(i18n.language))
                  : addonPackages
              }
              planCategory={planCategory}
              selectedPlan={selectedPlan}
              subscription={subscription}
              currentPlanData={getCurrentPlanRequest.data?.data.data}
              isLoadingProducts={getProductListRequest.isLoading || getSubscriptionProductsRequest.isLoading}
              processingPayment={processingPayment}
              hideActionPanel={hideActionPanel}
              onPlanCategoryChange={handlePlanCategoryChange}
              onPlanSelect={handlePlanSelect}
              onCancelSelection={handleCancelSelection}
              onChangePlan={handleChangePlan}
              onStripePortalRedirect={handleStripePortalRedirect}
              stripeUrl={getStripePortalUrlRequest.data?.data?.data}
              userId={user?.uid}
              isEnterpriseUser={isEnterpriseUser}
            />
          )}

          {/* 暂时隐藏设置tab内容，下个版本再上线
          {activeTab === 'settings' && (
            <Settings />
          )}
          */}

          {activeTab === 'bill-management' && (
            <div className="bill-content">
              <div className="bill-title">{t('accountManage')}</div>
              <div className="bill-header">
                <div className="header-left-top-text">
                  <div className="header-text">{t('totalTimeRemaining')}：</div>
                  <div className="header-text-time">{t('minutes', { time: total })}</div>
                </div>
                <div className="buy-text" onClick={() => openPage('/pricing')}>
                  {t('topUp')}
                </div>
              </div>
              <div className="bill-content-container">
                <div className="bill-menu-contnet">
                  <div className="bill-menus">
                    <div
                      className={clsx('bill-menu-item', billSubTab === 0 && 'activity')}
                      onClick={() => setBillSubTab(0)}
                    >
                      {t('buyData')}
                    </div>
                    <div
                      className={clsx('bill-menu-item', billSubTab === 1 && 'activity')}
                      onClick={() => setBillSubTab(1)}
                    >
                      {t('usage')}
                    </div>
                  </div>
                </div>

                <div className="table-content">
                  {billSubTab === 0 ? (
                    <div className="source-table-content">
                      {getBoughtProductRequest.isLoading && <Spin className="!block mx-auto" size="large" />}
                      {getBoughtProductRequest.data?.data.data.records.length === 0 && <Empty text={t('noBuy')} />}
                      {getBoughtProductRequest.data?.data.data.records.length > 0 && (
                        <>
                          <Table
                            rowKey="user_product_id"
                            pagination={false}
                            columns={buyHistoryColumns}
                            loading={getBoughtProductRequest.isLoading}
                            dataSource={getBoughtProductRequest.data?.data.data.records}
                            className="custom-table"
                            locale={{ emptyText: t('NoData') }}
                            bordered={false}
                          />
                          <Pagination
                            className={styles.paginationRecord}
                            pageSize={buyHistorySize}
                            showSizeChanger={false}
                            current={buyHistoryCurr}
                            total={buyHistoryTotal}
                            hideOnSinglePage
                            onChange={(page, pageSize) => {
                              setBuyHistoryCurr(page);
                              setBuyHistorySize(pageSize);
                            }}
                          />
                        </>
                      )}
                    </div>
                  ) : (
                    <UsageHistory
                      usageCurr={usageCurr}
                      usageTotal={usageTotal}
                      usageSize={usageSize}
                      onUsagePaginationChange={handleUsagePaginationChange}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Cancellation Reason Modal */}
        <CancellationReasonModal
          open={showReasonModal}
          onClose={handleReasonModalClose}
          onSuccess={handleReasonSubmitSuccess}
          userEmail={user?.email || ''}
        />

        {/* Cancel Subscription Modal */}
        <CancelSubscriptionModal
          visible={showCancelModal}
          subscription={subscription}
          onCancel={handleCancelModalClose}
          onConfirm={handleCancelSubscription}
          loading={cancellingSubscription}
          currentPlanData={getCurrentPlanRequest.data?.data.data}
          subscriptionProducts={getSubscriptionProductsRequest.data?.data || []}
        />
      </div>
    </div>
  );
}

export default EnhancedAccount;
